/* eslint-disable react/prop-types */
import React from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    IconButton,
    Paper,
    Typography,
    Box,
    Avatar,
    Chip,
    CircularProgress,
    alpha,
    useMediaQuery
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { Timeline, TimelineItem, TimelineSeparator, TimelineConnector, TimelineContent, TimelineDot } from '@mui/lab';
import { Close as CloseIcon, Timeline as TimelineIcon, Assignment as AssignmentIcon } from '@mui/icons-material';

import PropTypes from 'prop-types';

const burgundyColor = '#800020';

const trafficLightColors = {
    APPROVED: {
        main: '#1C4B45', // Dark teal/green from the image
        light: '#A5C6C3',
        dark: '#0F322E',
        gradient: 'linear-gradient(135deg, #1C4B45, #0F322E)',
        glow: '0 0 20px rgba(28, 75, 69, 0.2)'
    },
    DECLINED: {
        main: '#C62828', // Deep professional red
        light: '#EF9A9A',
        dark: '#B71C1C',
        gradient: 'linear-gradient(135deg, #C62828, #B71C1C)',
        glow: '0 0 20px rgba(198, 40, 40, 0.2)'
    },
    PENDING: {
        main: '#1B2F33', //  Dark navy/teal
        light: '#2D4A4F',
        dark: '#0F1D20',
        gradient: 'linear-gradient(135deg, #1B2F33, #0F1D20)',
        glow: '0 0 20px rgba(27, 47, 51, 0.2)'
    }
};

const fallbackStatusColors = {
    main: '#40565C',
    dark: '#2F4145',
    gradient: 'linear-gradient(135deg, #40565C, #2F4145)',
    glow: '0 0 20px rgba(64, 86, 92, 0.2)'
};

export const TripTimelineDialog = ({ open, handleClose, tripData, statuses = [], loading = false, error = '' }) => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

    const formatDate = (dateString) => {
        if (!dateString) return '';

        const date = new Date(dateString);
        const day = date.getDate();
        const month = date.toLocaleString('en-US', { month: 'short' });
        const year = date.getFullYear();

        // For time formatting
        const hour = date.getHours().toString().padStart(2, '0');
        const minute = date.getMinutes().toString().padStart(2, '0');

        // If you need time included
        const formattedDateTime = `${day} ${month} ${year}, ${hour}:${minute}`;

        return formattedDateTime; // or return formattedDateTime if you need time included
    };

    const TimelineCard = ({ title, content, icon, color }) => (
        <Paper
            elevation={4}
            sx={{
                p: 2,
                borderRadius: 2,
                background: 'white',
                position: 'relative',
                overflow: 'hidden',
                transition: 'all 0.3s ease',
                boxShadow: `0 4px 12px ${alpha(trafficLightColors[color]?.main || '#40565C', 0.2)}`,
                '&:hover': {
                    transform: 'translateY(-2px)',
                    boxShadow: trafficLightColors[color]?.glow || '0 0 20px rgba(64, 86, 92, 0.2)'
                },
                '&::before': {
                    content: '""',
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    right: 0,
                    height: '4px',
                    background: trafficLightColors[color]?.gradient || 'linear-gradient(135deg, #40565C, #2F4145)'
                }
            }}
        >
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 1.5 }}>
                <Avatar
                    sx={{
                        bgcolor: alpha(trafficLightColors[color]?.main || '#40565C', 0.1),
                        mr: 1.5,
                        transition: 'all 0.3s ease',
                        '&:hover': {
                            transform: 'rotate(360deg)',
                            bgcolor: trafficLightColors[color]?.main || '#40565C',
                            '& > *': {
                                color: 'white'
                            }
                        }
                    }}
                >
                    {React.cloneElement(icon, {
                        sx: {
                            // Change icon color based on status
                            color: title.includes('PENDING') ? burgundyColor : trafficLightColors[color]?.main
                        }
                    })}
                </Avatar>
                <Typography
                    variant="h6"
                    sx={{
                        fontWeight: 600,
                        // Change color based on status
                        color: title.includes('PENDING') ? burgundyColor : trafficLightColors[color]?.main || '#40565C',
                        textShadow: `0 0 1px ${alpha(trafficLightColors[color]?.main || '#40565C', 0.1)}`
                    }}
                >
                    {title}
                </Typography>
            </Box>
            <Box>{content}</Box>
        </Paper>
    );

    const getTimelineItems = () =>
        statuses.map((status) => {
            const statusColors = trafficLightColors[status.status] || fallbackStatusColors;

            return {
                title: `Status: ${status.status}`,
                content: (
                    <Box>
                        <Typography variant="subtitle2" sx={{ color: statusColors.dark }}>
                            Approver: {status.approvedBy?.firstName} {status.approvedBy?.lastName}
                        </Typography>
                        <Box
                            sx={{
                                mt: 1,
                                p: 2,
                                borderRadius: 1,
                                bgcolor: alpha(statusColors.main, 0.05),
                                border: `1px solid ${alpha(statusColors.main, 0.1)}`
                            }}
                        >
                            <Typography
                                variant="subtitle2"
                                sx={{
                                    color: statusColors.dark,
                                    mb: 0.5,
                                    fontWeight: 600
                                }}
                            >
                                Remarks
                            </Typography>
                            <Typography
                                variant="body2"
                                sx={{
                                    color: 'text.secondary',
                                    whiteSpace: 'pre-wrap',
                                    wordBreak: 'break-word',
                                    lineHeight: 1.6,
                                    maxHeight: '200px',
                                    overflowY: 'auto',
                                    '&::-webkit-scrollbar': {
                                        width: '8px'
                                    },
                                    '&::-webkit-scrollbar-track': {
                                        background: alpha(statusColors.main, 0.05)
                                    },
                                    '&::-webkit-scrollbar-thumb': {
                                        background: alpha(statusColors.main, 0.2),
                                        borderRadius: '4px',
                                        '&:hover': {
                                            background: alpha(statusColors.main, 0.3)
                                        }
                                    },
                                    padding: 1
                                }}
                            >
                                {status.remarks || 'No remarks provided'}
                            </Typography>
                        </Box>
                    </Box>
                ),
                icon: <AssignmentIcon />,
                color: status.status,
                date: formatDate(status.approvedAt || status.createdAt)
            };
        });

    const timelineItems = getTimelineItems();

    const renderTimelineContent = () => {
        if (loading) {
            return (
                <StackState>
                    <CircularProgress size={36} />
                    <Typography color="text.secondary">Loading approval history...</Typography>
                </StackState>
            );
        }

        if (error) {
            return (
                <StackState>
                    <Typography color="error.main" fontWeight={600}>
                        {error}
                    </Typography>
                </StackState>
            );
        }

        if (timelineItems.length === 0) {
            return (
                <StackState>
                    <TimelineIcon sx={{ fontSize: 48, color: 'text.disabled' }} />
                    <Typography variant="h6">No approval history recorded</Typography>
                    <Typography color="text.secondary" textAlign="center">
                        This request does not have any approval events yet.
                    </Typography>
                    {tripData?.status && <Chip label={`Current status: ${tripData.status}`} color="primary" variant="outlined" />}
                </StackState>
            );
        }

        return (
            <Timeline position={isMobile ? 'right' : 'alternate'} sx={{ p: isMobile ? 0 : 2 }}>
                {timelineItems.map((item, index) => (
                    <TimelineItem key={index}>
                        <TimelineSeparator>
                            <TimelineDot
                                sx={{
                                    bgcolor: (trafficLightColors[item.color] || fallbackStatusColors).main,
                                    p: isMobile ? 1 : 1.5
                                }}
                            >
                                {React.cloneElement(item.icon, { sx: { fontSize: isMobile ? 18 : 24 } })}
                            </TimelineDot>
                            {index < timelineItems.length - 1 && (
                                <TimelineConnector
                                    sx={{
                                        background: (trafficLightColors[item.color] || fallbackStatusColors).gradient
                                    }}
                                />
                            )}
                        </TimelineSeparator>
                        <TimelineContent sx={{ px: isMobile ? 1 : 2, py: isMobile ? 1 : 2 }}>
                            <TimelineCard title={item.title} content={item.content} icon={item.icon} color={item.color} />
                            <Box sx={{ mt: 1, textAlign: isMobile || index % 2 === 0 ? 'right' : 'left' }}>
                                <Typography
                                    variant="caption"
                                    sx={{
                                        display: 'inline-block',
                                        px: 1,
                                        py: 0.5,
                                        bgcolor: alpha((trafficLightColors[item.color] || fallbackStatusColors).main, 0.1),
                                        borderRadius: 1,
                                        color: (trafficLightColors[item.color] || fallbackStatusColors).main,
                                        fontWeight: 600
                                    }}
                                >
                                    {item.date}
                                </Typography>
                            </Box>
                        </TimelineContent>
                    </TimelineItem>
                ))}
            </Timeline>
        );
    };

    return (
        <Dialog
            open={open}
            onClose={handleClose}
            maxWidth="md"
            fullWidth
            fullScreen={isMobile}
            PaperProps={{
                sx: {
                    borderRadius: isMobile ? 0 : 3,
                    bgcolor: '#F8FAFC',
                    boxShadow: '0 8px 32px rgba(0, 0, 0, 0.1)'
                }
            }}
        >
            <DialogTitle
                sx={{
                    background: trafficLightColors.APPROVED.gradient,
                    color: 'white',
                    py: isMobile ? 2 : 3,
                    position: 'relative'
                }}
            >
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <TimelineIcon sx={{ mr: isMobile ? 1 : 2, fontSize: isMobile ? 24 : 28 }} />
                        <Box>
                            <Typography variant={isMobile ? 'subtitle1' : 'h6'} sx={{ fontWeight: 600, color: 'white' }}>
                                Trip Request Timeline
                            </Typography>
                            <Typography variant="caption" sx={{ opacity: 0.8, color: 'white', display: 'block' }}>
                                {tripData?.sequenceNumber}
                            </Typography>
                        </Box>
                    </Box>
                    <IconButton onClick={handleClose} sx={{ color: 'white' }}>
                        <CloseIcon />
                    </IconButton>
                </Box>
            </DialogTitle>
            <DialogContent sx={{ p: isMobile ? 2 : 4, minHeight: 220 }}>{renderTimelineContent()}</DialogContent>
        </Dialog>
    );
};

TripTimelineDialog.propTypes = {
    open: PropTypes.bool.isRequired,
    handleClose: PropTypes.func.isRequired,
    tripData: PropTypes.shape({
        sequenceNumber: PropTypes.string
    }),
    statuses: PropTypes.arrayOf(
        PropTypes.shape({
            status: PropTypes.string,
            approvalLevel: PropTypes.string,
            approvedBy: PropTypes.shape({
                firstName: PropTypes.string,
                lastName: PropTypes.string
            }),
            remarks: PropTypes.string,
            approvedAt: PropTypes.string,
            createdAt: PropTypes.string
        })
    ),
    loading: PropTypes.bool,
    error: PropTypes.string
};

const StackState = ({ children }) => (
    <Box
        sx={{
            minHeight: 180,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 1.5
        }}
    >
        {children}
    </Box>
);

StackState.propTypes = {
    children: PropTypes.node.isRequired
};
